import { useState } from 'react'
import type { Product, PurchaseType } from '../../types'
import { getAvailablePurchaseTypes } from '../../types'
import { TypeSelectPopup } from './TypeSelectPopup'
import { CUSTOMER_TYPE_LABELS } from './customerTypeLabels'

interface ProductRowProps {
  product: Product
  /** Total quantity of this product across ALL purchase types in the cart. */
  cartCount: number
  onAddToCart: (product: Product, purchaseType: PurchaseType, quantity: number) => void
}

/**
 * Compact wholesale ordering row (~80-110px). Shows the product name first,
 * a type button (price list lives inside the popup, not the row), a compact
 * quantity stepper and a cart icon button. No type is pre-selected: the
 * customer must pick one before the add-to-cart action is accepted.
 * Cart lines stay split per (product, purchaseType) in the drawer — this
 * row only aggregates the count for its contextual indicator.
 */
export function ProductRow({ product, cartCount, onAddToCart }: ProductRowProps) {
  const availableTypes = getAvailablePurchaseTypes(product)
  const hasValidPrice = availableTypes.length > 0
  const [selectedType, setSelectedType] = useState<PurchaseType | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [typeHint, setTypeHint] = useState<string | null>(null)

  const incrementQuantity = () => setQuantity((q) => Math.min(999, q + 1))
  const decrementQuantity = () => setQuantity((q) => Math.max(1, q - 1))
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    setQuantity(Number.isNaN(value) ? 1 : Math.min(999, Math.max(1, value)))
  }

  const handleSelectType = (type: PurchaseType) => {
    setSelectedType(type)
    setTypeHint(null)
    setIsPopupOpen(false)
  }

  const handleAddToCart = () => {
    if (!hasValidPrice) return
    if (!selectedType) {
      setTypeHint('Silakan pilih tipe pembelian terlebih dahulu.')
      setIsPopupOpen(true)
      return
    }
    onAddToCart(product, selectedType, quantity)
    setQuantity(1)
  }

  return (
    <li className="product-row">
      <div className="product-row-main">
        <div className="product-row-info">
          <h3 className="product-row-name">{product.name}</h3>
          {product.category && (
            <p className="product-row-category">{product.category}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => hasValidPrice && setIsPopupOpen(true)}
          disabled={!hasValidPrice}
          className={`product-row-type-btn ${selectedType ? 'chosen' : ''}`}
          aria-haspopup="dialog"
          aria-label={
            hasValidPrice
              ? selectedType
                ? `Tipe pembelian: ${CUSTOMER_TYPE_LABELS[selectedType]}. Ketuk untuk mengubah.`
                : `Pilih tipe pembelian untuk ${product.name}`
              : `${product.name} tidak tersedia`
          }
        >
          <span className="product-row-type-label">
            {hasValidPrice ? (selectedType ? CUSTOMER_TYPE_LABELS[selectedType] : 'Pilih Tipe') : 'Tidak Tersedia'}
          </span>
          {hasValidPrice && (
            <svg className="w-4 h-4 product-row-type-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </div>

      <div className="product-row-controls">
        <div className="quantity-stepper" role="group" aria-label={`Jumlah untuk ${product.name}`}>
          <button
            type="button"
            onClick={decrementQuantity}
            disabled={quantity <= 1}
            className="quantity-stepper-btn"
            aria-label="Kurangi jumlah"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <input
            type="number"
            value={quantity}
            onChange={handleQuantityChange}
            onBlur={(e) => {
              const v = parseInt(e.target.value, 10)
              setQuantity(Number.isNaN(v) ? 1 : Math.min(999, Math.max(1, v)))
            }}
            min={1}
            max={999}
            className="quantity-stepper-value"
            inputMode="numeric"
            aria-label="Jumlah"
          />
          <button
            type="button"
            onClick={incrementQuantity}
            disabled={quantity >= 999}
            className="quantity-stepper-btn"
            aria-label="Tambah jumlah"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={!hasValidPrice}
          className="product-row-cart-btn"
          aria-label={`Tambah ${product.name} ke keranjang`}
          title="Tambah ke keranjang"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        </button>
      </div>

      {typeHint || cartCount > 0 ? (
        <div className="product-row-meta">
          {typeHint && !selectedType && (
            <p className="product-row-hint" role="alert">
              {typeHint}
            </p>
          )}
          {cartCount > 0 && (
            <p className="product-row-in-cart">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              {cartCount} item di keranjang
            </p>
          )}
        </div>
      ) : null}

      {isPopupOpen && hasValidPrice && (
        <TypeSelectPopup
          product={product}
          selectedType={selectedType}
          onSelect={handleSelectType}
          onClose={() => setIsPopupOpen(false)}
        />
      )}
    </li>
  )
}
